import {ModelSetOptions} from 'backbone';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import {BufferViewLayout} from 'buffers/BufferComps';
import Protein from 'molbio/aa-sequences/AaSequence';
import {LinearLayout} from 'molbio/maps/linear/LinearLayout';
import {ProteinLinearMapState} from 'molbio/maps/MapStates';
import SinglePolymerMapTabState, {
  SinglePolymerMapTabStateAttributes,
} from 'molbio/tabstates/SinglePolymerMapTabState';
import {TabStateDefaults} from 'Navigation';
import {constructOptions, VisibilityOptions} from 'settings/visibility-options/VisibilityOptions';
import * as vo from 'settings/visibility-options/VisibilityOptions';

export interface ProteinMapTabStateAttributes
  extends SinglePolymerMapTabStateAttributes<Protein, ProteinLinearMapState> {
  visibilityOptions: VisibilityOptions;
  bufferViewLayout: BufferViewLayout | null;
  layout: LinearLayout;
}

interface ProteinMapTabState {
  get<K extends keyof ProteinMapTabStateAttributes>(key: K): ProteinMapTabStateAttributes[K];
  set<K extends keyof ProteinMapTabStateAttributes>(
    attributeName: K,
    value: ProteinMapTabStateAttributes[K],
    options?: ModelSetOptions
  ): ProteinMapTabState;
  set(obj: Partial<ProteinMapTabStateAttributes>): ProteinMapTabState;
}

class ProteinMapTabState extends SinglePolymerMapTabState<Protein, ProteinLinearMapState> {
  mapState(options: {polymer: Protein; visibilityOptions: VisibilityOptions}): ProteinLinearMapState {
    return new ProteinLinearMapState(options);
  }

  msVisibilityOptions(): VisibilityOptions {
    const editableOptions = this.get('visibilityOptions')
      .filter((opt) => opt.get('editable'))
      .map((opt) => opt.get('name'));
    return this.get('visibilityOptions').makeLocalSync({editableOptions});
  }

  override defaults(): TabStateDefaults & Partial<ProteinMapTabStateAttributes> {
    const visibilityOptions = _.compact([
      {base: vo.MAXIMISE, visible: true, editable: false, sync: false},
      {base: vo.PROTEIN_AXIS, visible: true, editable: false, sync: true},
      {base: vo.DNA, visible: true, editable: false, sync: true},
      {base: vo.ANNOTATIONS, visible: true, editable: true, sync: true},
      BenchlingConfig.MOLBIO_ENABLE_AA_PARTS
        ? {base: vo.PART_ANNOTATIONS, visible: true, editable: true, sync: true}
        : null,
      {base: vo.SEARCH_AMINO_ACIDS, visible: true, editable: false, sync: true},
      {base: vo.PROTEIN_COLORING, visible: true, editable: true, sync: true},
      {base: vo.PROTEIN_HYDROPHOBICITY_GRAPH, visible: false, editable: true, sync: true},
      {base: vo.PROTEIN_PI_GRAPH, visible: false, editable: true, sync: true},
      BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENGINEERING_TOOLS
        ? {
            base: vo.ANTIBODY_NUMBERING,
            visible: false,
            editable: true,
            sync: true,
          }
        : null,
      BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENGINEERING_TOOLS
        ? {
            base: vo.ANTIBODY_CDR_ANNOTATIONS,
            visible: false,
            editable: true,
            sync: true,
          }
        : null,
      BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENGINEERING_TOOLS
        ? {
            base: vo.PROTEIN_LIABILITY_SITES,
            visible: false,
            editable: true,
            sync: true,
          }
        : null,
      BenchlingConfig.MOLBIO_ENABLE_AA_PARTS ? {base: vo.PARTS, visible: true, editable: false} : null,
    ]);
    return {
      ...super.defaults(),
      closeable: false,
      title: 'Sequence Map',
      bufferViewLayout: null,
      visibilityOptions: constructOptions('linear', visibilityOptions),
      preferenceKey: 'protein-map',
    };
  }
}

export default ProteinMapTabState;
